import classNames from 'classnames'
import React, { useCallback, useMemo } from 'react'
import { isFirefox } from 'react-device-detect'
import countryCodes from '../../constants/countryCodes.json'
import { isNullOrWhiteSpace } from '../../utilities/string'
import { Dropdown, DropdownProps, OnRenderOptionCallback } from './Dropdown'
import { Input, InputProps } from './Input'
import { Label } from './Label'

type Country = {
  callingCode: string
  isoCode: string
  name: string
}

const countriesByCallingCode = Object.keys(countryCodes).reduce<Record<string, Country>>(
  (acc, isoCode) => {
    const country = countryCodes[isoCode]

    const callingCode = `+${country.code}`

    acc[callingCode] = {
      callingCode,
      isoCode,
      name: country.name,
    }

    return acc
  },
  {}
)

const getIsoCodeWithoutSlash = (isoCode: string) => {
  const indexOfDash = isoCode.indexOf('-')

  if (indexOfDash !== -1) {
    isoCode = isoCode.substring(0, indexOfDash)
  }

  return isoCode
}

export type PhoneNumberProps = {
  dropdownProps: Omit<DropdownProps, 'options'>
  id?: string
  label?: string
  required?: boolean
  textInputProps: Omit<InputProps, 'defaultValue'>
  validation?: true | string
}

/** Note: This input creates separate 2 inputs, one for the country calling code dropdown, and one for the
 *  number text input. It is up to the caller to merge their values upon for example form submission.
 */
export const PhoneNumber = ({
  dropdownProps,
  id,
  label,
  required,
  textInputProps,
  validation,
}: PhoneNumberProps) => {
  const onRenderCountryOption = useCallback<OnRenderOptionCallback>(
    (value: string, elementProps, { label, focused, selected }) => {
      const country = countriesByCallingCode[value]

      const flagCdnIsoCode = getIsoCodeWithoutSlash(country.isoCode).toLowerCase()

      return (
        <li
          {...elementProps}
          className="mx-5 border-b border-b-neutral-lighter-alt group cursor-pointer whitespace-normal last:border-b-0"
        >
          <div
            className={classNames(
              'flex w-full items-center pl-5 pr-10 py-6 gap-5 transition-all duration-100',
              {
                'ring ring-primary/40 rounded-5': focused,
              }
            )}
          >
            <div
              className={classNames(
                'flex flex-none justify-center items-center w-30 h-[1.5625rem] border rounded-3 transition-all duration-200',
                {
                  'border-primary': selected,
                  'border-transparent group-hover:border-primary': !selected,
                }
              )}
            >
              <img
                alt={country.isoCode}
                className="aspect-[4/3]"
                loading="lazy"
                src={`https://flagcdn.com/${flagCdnIsoCode}.svg`}
                width={20}
              />
            </div>

            <div
              className={classNames(
                'basis-full flex-grow font-t text-t-300 transition-all duration-200',
                {
                  'text-primary': selected,
                  'text-secondary-darker': !selected,
                }
              )}
            >
              {label}
            </div>

            <div className="flex-none ml-auto pl-5 font-t text-t-300 text-shade-medium-dim">
              {value}
            </div>
          </div>
        </li>
      )
    },
    []
  )

  const onRenderCountryValue = useCallback((value: string) => {
    const country = countriesByCallingCode[value]

    const flagCdnIsoCode = getIsoCodeWithoutSlash(country.isoCode).toLowerCase()

    return (
      <div className="flex items-center">
        <img
          alt={country.isoCode}
          src={`https://flagcdn.com/${flagCdnIsoCode}.svg`}
          width={20}
        />

        <div className="font-t text-t-300 ml-5">{value}</div>
      </div>
    )
  }, [])

  const options = useMemo(
    () =>
      Object.keys(countriesByCallingCode).map(callingCode => {
        const country = countriesByCallingCode[callingCode]

        return {
          label: `${country.name} (${country.callingCode})`,
          value: callingCode,
        }
      }),
    []
  )

  return (
    <div className="w-full">
      {!isNullOrWhiteSpace(label) && (
        <Label
          additionalClassName="mb-10"
          htmlFor={textInputProps.name}
          required={required}
        >
          {label}
        </Label>
      )}

      <div className="relative flex">
        <Dropdown
          id={id ? `${id}-country` : undefined}
          {...dropdownProps}
          additionalClassName="flex-none"
          applyRelative={false}
          displayValidationMessage={false}
          flyoutMaxWidth="max-w-[15.625rem]"
          onRenderOption={onRenderCountryOption}
          onRenderValue={onRenderCountryValue}
          options={options}
          required={required}
          roundedBorders={{
            bottomLeft: true,
            topLeft: true,
          }}
          useNativeSelectInMobile={false}
          variant="button"
        />

        <Input
          id={id}
          {...textInputProps}
          // Set position relative on focus, due to z-index issues with the focus:ring
          additionalClassName={classNames({
            'focus-within:relative': isFirefox,
            '[&:has(:focus-visible)]:relative': !isFirefox,
          })}
          displayValidationMessage={false}
          required={required}
          roundedBorders={{
            bottomRight: true,
            topRight: true,
          }}
          type="tel"
        />
      </div>

      <div className="min-h-15 mt-5 text-secondary font-t text-t-200 leading-[15px]">
        {validation !== true && !isNullOrWhiteSpace(validation) && validation}
      </div>
    </div>
  )
}
